import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, FileText, GanttChart, Gift, Upload, Cpu, Building2 } from "lucide-react";
import { CORPS_COLORS } from "@/lib/corpsColors";

export default function Landing() {
  return (
    <div data-testid="landing-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-16">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 items-center">
          <div>
            <Badge
              data-testid="hero-badge"
              className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border-emerald-200 font-mono-tech uppercase tracking-wider text-[11px]"
            >
              <Gift className="w-3 h-3 mr-1.5" />
              Beta gratuite · Contre retour d'expérience
            </Badge>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F2137] mt-5 leading-[1.05] tracking-tight">
              Planning chantier{" "}
              <span className="relative inline-block">
                <span className="relative z-10">gratuit</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-[#F59E0B]/45 -z-0" />
              </span>{" "}
              : votre Gantt depuis un devis PDF.
            </h1>
            <p className="mt-5 text-slate-600 text-base sm:text-lg max-w-xl leading-relaxed">
              Le logiciel de planning chantier en ligne qui lit vos devis et génère automatiquement un diagramme de Gantt complet en 60 secondes. Corps d'état, dépendances, chemin critique inclus. Aucune inscription.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/nouveau" data-testid="cta-start">
                <Button
                  size="lg"
                  className="bg-[#F59E0B] hover:bg-[#D97706] text-[#0F2137] font-semibold h-12 px-6"
                >
                  Créer mon planning
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/historique" data-testid="cta-history">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-slate-300 h-12 px-6 font-semibold"
                >
                  Voir mes plannings
                </Button>
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
              {[
                { k: "10-20", v: "tâches / projet" },
                { k: "8", v: "corps d'état" },
                { k: "PDF · DOCX · XLSX", v: "formats", small: true },
              ].map((s, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-slate-200 bg-white p-3"
                  data-testid={`stat-${i}`}
                >
                  <div
                    className={`font-display font-bold text-[#0F2137] ${
                      s.small ? "text-sm" : "text-2xl"
                    }`}
                  >
                    {s.k}
                  </div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">
                    {s.v}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right visual */}
          <div className="relative">
            <div className="rounded-2xl border border-slate-200 bg-white shadow-xl p-5 relative overflow-hidden">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-display font-bold text-[#0F2137]">Villa Biarritz · 180 m²</div>
                  <div className="font-mono-tech text-[10px] uppercase tracking-wider text-slate-500">
                    Aperçu du Gantt
                  </div>
                </div>
                <Badge className="bg-[#0F2137] text-white hover:bg-[#0F2137]">Chemin critique</Badge>
              </div>
              <div className="space-y-2.5">
                {[
                  { name: "Terrassement", corps: "Gros Œuvre / Structure", w: 22, off: 0, crit: true },
                  { name: "Fondations", corps: "Gros Œuvre / Structure", w: 30, off: 15, crit: true },
                  { name: "Charpente", corps: "Charpente / Couverture", w: 26, off: 42, crit: true },
                  { name: "Menuiseries ext.", corps: "Menuiserie Extérieure", w: 20, off: 58, crit: false },
                  { name: "Électricité", corps: "Électricité / CFC", w: 32, off: 55, crit: false },
                  { name: "Plâtrerie", corps: "Plâtrerie / Isolation", w: 24, off: 70, crit: true },
                  { name: "Peinture", corps: "Revêtements / Peinture", w: 18, off: 82, crit: false },
                ].map((t, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-28 text-xs font-medium text-slate-700 truncate">{t.name}</div>
                    <div className="flex-1 h-6 relative bg-slate-100 rounded">
                      <div
                        className="absolute top-0 bottom-0 rounded"
                        style={{
                          left: `${t.off}%`,
                          width: `${t.w}%`,
                          background: CORPS_COLORS[t.corps],
                          boxShadow: t.crit ? "0 0 0 2px #0F2137 inset" : "none",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between font-mono-tech text-[10px] text-slate-500 uppercase tracking-wider">
                <span>J0</span>
                <span>Semaine 4</span>
                <span>Semaine 8</span>
                <span>Semaine 12</span>
              </div>
            </div>
            {/* Accent block */}
            <div className="absolute -top-3 -right-3 w-16 h-16 rounded-lg bg-[#F59E0B] rotate-6 -z-0" />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20">
        <div className="border-t border-slate-200 pt-12">
          <div className="font-mono-tech text-xs uppercase tracking-wider text-slate-500">
            Mode d'emploi
          </div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold text-[#0F2137] mt-2">
            Le planning chantier en ligne, en trois étapes.
          </h2>

          <div className="mt-8 grid md:grid-cols-3 gap-5">
            {[
              {
                icon: Building2,
                num: "01",
                title: "Décrivez votre projet",
                text: "Type de bâtiment, surface, date de démarrage, contraintes — quelques champs suffisent.",
              },
              {
                icon: Upload,
                num: "02",
                title: "Déposez votre devis PDF",
                text: "Devis d'entreprises, CCTP, mémoires techniques en PDF, Word ou Excel. Le logiciel lit tout automatiquement.",
              },
              {
                icon: GanttChart,
                num: "03",
                title: "Obtenez votre planning Gantt",
                text: "Diagramme de Gantt interactif, tâches ordonnées par corps d'état, dépendances, chemin critique. Prêt à envoyer aux entreprises.",
              },
            ].map((s, i) => (
              <div
                key={i}
                data-testid={`step-${i}`}
                className="rounded-xl border border-slate-200 bg-white p-6 hover:border-[#F59E0B] transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="w-11 h-11 rounded-lg bg-[#0F2137] flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-[#F59E0B]" />
                  </div>
                  <div className="font-mono-tech text-2xl font-bold text-slate-200">{s.num}</div>
                </div>
                <h3 className="font-display font-semibold text-lg text-[#0F2137] mt-4">{s.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
